import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Tension Contour Stripe v1 lexicon entries — one tool-level entry + one per
 * bucket (rest / low / medium / high / peak). Bowie-authored and locked
 * verbatim; do not edit the wording without Bowie review.
 *
 * Spec: docs/superpowers/specs/2026-06-03-tension-contour-stripe-model-spec.md §6
 */
export const TENSION_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'tension-stripe',
    category: 'tension',
    displayName: 'Tension contour',
    displayNameShort: 'Tension',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Colors show how much each chord wants to move — calm colors feel at rest, warm colors pull toward home.',
      intermediate: 'The stripe maps harmonic tension per chord — tonic at rest, predominants in motion, dominants pulling to resolve.',
      advanced: 'Per-chord harmonic-tension contour — functional base (T→P→D) plus chordal dissonance and chromatic color, modal-attenuated where no leading-tone pull exists.',
    },
    tagLabel: { beginner: 'tension colors', intermediate: 'tension contour', advanced: 'harmonic tension' },
  },
  {
    id: 'tension-rest',
    category: 'tension',
    displayName: 'At rest',
    displayNameShort: 'Rest',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Home base — this chord feels settled and complete.',
      intermediate: 'Tonic at rest — the resolution point other chords pull toward.',
      advanced: 'Tonic function — zero functional tension; the contour\'s resolution floor.',
    },
    tagLabel: { beginner: 'at rest', intermediate: 'tonic rest', advanced: 'resolution' },
  },
  {
    id: 'tension-low',
    category: 'tension',
    displayName: 'Nearly home',
    displayNameShort: 'Low',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Nearly settled — a gentle lean away from home.',
      intermediate: 'Tonic substitute — stable, but not quite home (vi or iii standing in for I).',
      advanced: 'Tonic-substitute function — low tension; shares two tones with I but lacks full arrival.',
    },
    tagLabel: { beginner: 'nearly home', intermediate: 'tonic substitute', advanced: 'weak tonic' },
  },
  {
    id: 'tension-medium',
    category: 'tension',
    displayName: 'On the move',
    displayNameShort: 'Medium',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'On the move — this chord steps away from home and starts the journey.',
      intermediate: 'Predominant — departure chords (IV, ii) that set up the dominant.',
      advanced: 'Predominant function — directed motion toward V; moderate tension from departure, not yet demanding resolution.',
    },
    tagLabel: { beginner: 'moving', intermediate: 'predominant', advanced: 'departure' },
  },
  {
    id: 'tension-high',
    category: 'tension',
    displayName: 'Pulling home',
    displayNameShort: 'High',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Pulling home — this chord leans hard toward the home chord.',
      intermediate: 'Dominant — the tritone and leading tone pull strongly back to the tonic.',
      advanced: 'Dominant function — leading-tone + tritone instability demanding tonic resolution; the contour\'s peak in diatonic music.',
    },
    tagLabel: { beginner: 'pulling home', intermediate: 'dominant', advanced: 'dominant tension' },
  },
  {
    id: 'tension-peak',
    category: 'tension',
    displayName: 'Maximum pull',
    displayNameShort: 'Peak',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Maximum pull — extra color makes the pull home even stronger.',
      intermediate: 'Altered or chromatic dominant — alterations (♭9, ♯5) or substitutions stack tension on the dominant.',
      advanced: 'Chromatically intensified dominant — alterations, tritone substitution, or secondary-dominant chromaticism above the functional peak.',
    },
    tagLabel: { beginner: 'max tension', intermediate: 'altered dominant', advanced: 'chromatic peak' },
  },
]
